/**
 * Fonte única de navegação: rotas, grupos e módulos.
 *
 * Reconstrói o `ROUTES` final do protótipo
 * (`prototipo/Mandato360-prototipo-identidade_3.html`, base em proto:508-535)
 * após as seis mutações de tempo de execução que o protótipo aplica:
 *
 * - proto:1388 remove `emendas`, `reguas`, `eleg-cand`, `matriz`
 * - proto:1462 insere um `perfil` provisório antes de `eleg`
 * - proto:1561-1566 remove esse `perfil` provisório e insere o grupo
 *   "OTO · Estratégia" (`perfil`, `fontes`, `estrategias`, `agentes`)
 *   antes de `eleg`
 * - proto:1758 remove `eleg-bn`
 * - proto:1808 remove `falando`
 *
 * As seis rotas removidas do menu continuam existindo como rota acessível
 * (`noMenu: true`) — o destino de cada uma é decidido na Task 2.3.
 *
 * `rota` guarda o identificador curto do protótipo (`r:` do ROUTES);
 * `caminho` guarda o caminho real usado pelo react-router
 * (ver `apps/web/src/app/router.tsx`), que diverge do protótipo em
 * `solic`→`/solicitacoes`, `terr`→`/territorio`, `eleg-cand`→`/historico`,
 * `controle`→`/controle-votacao`, `eleg-ing`→`/integracoes` e
 * `audit`→`/auditoria`.
 *
 * O campo `grupo` só é preenchido no primeiro item de cada grupo, como no
 * protótipo (proto:1870, `rotaGrupo`) — `grupoDaRota` reproduz essa varredura.
 */

import type { ChaveModulo } from './modulos';
import { MODULOS } from './modulos';
import type { IconeRota } from '@/design-system/icons';

export { MOD_COLORS, MODULOS } from './modulos';
export type { ChaveModulo, Modulo } from './modulos';

export interface Rota {
  rota: string;
  caminho: string;
  nome: string;
  grupo?: string;
  icone: IconeRota;
  /**
   * O módulo exigido por esta tela, não o agrupamento comercial do menu
   * (`grupo`). É o que a API realmente exige (`@RequiredModulo` do
   * controller que a tela consome) — ver o comentário em
   * `apps/web/src/app/Sidebar.tsx:135-137`, que é a autoridade sobre esse
   * requisito. Por isso `/territorio` e `/campanhas`, embora fiquem no
   * grupo "Inteligência Eleitoral", exigem `gab` (não `eleg`): seus
   * controllers respondem 200 para quem só tem o módulo Gabinete360.
   * Confundir esse campo com `grupo` esconderia, do bloqueio de acesso,
   * telas que a API de fato libera.
   */
  modulo?: 'eleg' | 'gab' | 'oto';
  lock?: string;
  noMenu?: boolean;
}

export const ROTAS: Rota[] = [
  { rota: 'dash', caminho: '/dashboard', nome: 'Resumo', grupo: 'Gestão', icone: 'dash' },
  { rota: 'notif', caminho: '/notificacoes', nome: 'Notificações', icone: 'notif' },

  { rota: 'contatos', caminho: '/contatos', nome: 'Contatos', grupo: 'Relacionamento', icone: 'contatos', modulo: 'gab' },
  { rota: 'liderancas', caminho: '/liderancas', nome: 'Lideranças', icone: 'liderancas', modulo: 'gab' },
  { rota: 'datas', caminho: '/datas', nome: 'Aniversários e datas', icone: 'datas', modulo: 'gab' },

  { rota: 'solic', caminho: '/solicitacoes', nome: 'Solicitações', grupo: 'Atendimento', icone: 'solic', modulo: 'gab' },
  { rota: 'agenda', caminho: '/agenda', nome: 'Agenda', icone: 'agenda', modulo: 'gab' },
  { rota: 'visitas', caminho: '/visitas', nome: 'Agenda interna', icone: 'visitas', modulo: 'gab' },

  { rota: 'acoes', caminho: '/acoes', nome: 'Ações parlamentares', grupo: 'Atuação', icone: 'acoes', modulo: 'gab' },
  { rota: 'emendas', caminho: '/emendas', nome: 'Emendas e MAPPS', icone: 'emendas', modulo: 'gab', noMenu: true },
  { rota: 'reguas', caminho: '/reguas', nome: 'Réguas de relacionamento', icone: 'reguas', modulo: 'gab', noMenu: true },

  { rota: 'perfil', caminho: '/perfil', nome: 'Perfil & persona', grupo: 'OTO · Estratégia', icone: 'perfil', modulo: 'oto' },
  { rota: 'fontes', caminho: '/fontes', nome: 'Fontes & conhecimento', icone: 'fontes', modulo: 'oto' },
  { rota: 'estrategias', caminho: '/estrategias', nome: 'Estratégias', icone: 'estrategias', modulo: 'oto' },
  { rota: 'agentes', caminho: '/agentes', nome: 'Meus agentes', icone: 'agentes', modulo: 'oto' },

  { rota: 'eleg', caminho: '/eleg', nome: 'Visão geral eleitoral', grupo: 'Inteligência Eleitoral', icone: 'eleg' },
  { rota: 'terr', caminho: '/territorio', nome: 'Perfil do território', icone: 'terr', modulo: 'gab' },
  { rota: 'campanhas', caminho: '/campanhas', nome: 'Campanhas', icone: 'campanhas', modulo: 'gab' },
  { rota: 'eleg-cand', caminho: '/historico', nome: 'Histórico eleitoral', icone: 'eleg-cand', modulo: 'eleg', noMenu: true },
  { rota: 'controle', caminho: '/controle-votacao', nome: 'Controle de votação', icone: 'controle', modulo: 'eleg' },
  { rota: 'matriz', caminho: '/matriz', nome: 'Matriz de performance', icone: 'matriz', noMenu: true },
  { rota: 'eleg-bn', caminho: '/brancos-nulos', nome: 'Brancos e nulos', icone: 'eleg-bn', noMenu: true },
  { rota: 'pesquisas', caminho: '/pesquisas', nome: 'Pesquisas', icone: 'pesquisas', modulo: 'eleg' },
  { rota: 'falando', caminho: '/falando', nome: 'O que estão falando', icone: 'falando', noMenu: true },
  { rota: 'alertas', caminho: '/alertas', nome: 'Alertas estratégicos', icone: 'alertas', modulo: 'eleg' },
  { rota: 'agente', caminho: '/agente', nome: 'Agente analista', icone: 'agente', modulo: 'oto' },
  { rota: 'eleg-ing', caminho: '/integracoes', nome: 'Integrações e cargas', icone: 'eleg-ing', lock: 'risco' },

  { rota: 'admin', caminho: '/admin', nome: 'Equipe e módulos', grupo: 'Administração', icone: 'admin' },
  { rota: 'config', caminho: '/config', nome: 'Configuração do gabinete', icone: 'config' },
  { rota: 'audit', caminho: '/auditoria', nome: 'Auditoria', icone: 'audit' },
  { rota: 'outbox', caminho: '/outbox', nome: 'Eventos & Outbox', icone: 'outbox' },
];

export function grupoDaRota(caminho: string): string {
  let g = '';
  for (const r of ROTAS) {
    if (r.grupo) g = r.grupo;
    if (r.caminho === caminho) return g;
  }
  // Caminho não catalogado: nenhum grupo é dono dele. Devolver `g` aqui
  // devolveria o último grupo acumulado na varredura ('Administração'),
  // fazendo `moduloDaRota` de uma rota desconhecida cair sempre em 'gab' —
  // nunca `null`, apesar da assinatura prometer isso.
  return '';
}

export function moduloDaRota(caminho: string): ChaveModulo | null {
  const g = grupoDaRota(caminho);
  return (Object.keys(MODULOS) as ChaveModulo[]).find((k) => MODULOS[k].grupos.includes(g)) ?? null;
}

export function rotasDoModulo(k: ChaveModulo | null): Rota[] {
  const visiveis = ROTAS.filter((r) => !r.noMenu);
  if (!k) return visiveis;
  return visiveis.filter((r) => MODULOS[k].grupos.includes(grupoDaRota(r.caminho)));
}

export function rotuloDaRota(caminho: string): string {
  return ROTAS.find((r) => r.caminho === caminho)?.nome ?? '';
}
